import { FC, useCallback, useEffect, useState } from "react";
import { Button, Layout, Modal, Spin, message } from "../../design-system";
import {
    deactivateUser,
    inviteUsers,
    listUsers,
    reactivateUser,
    resendInvitation,
    setUserRoles,
} from "../../common/api/UserClient";
import { handleError } from "../../common/ErrorHandling";
import { useUserState } from "../../common/UserContext";
import { User, UserRole } from "../../types/UserGroup";
import { InviteUsersModal } from "../../common/components/InviteUsersModal";
import { MembersTable } from "./MembersTable";
import classes from "./MembersView.module.less";

const { Header, Content } = Layout;

/** The organization's Members page, for its admins. */
export const MembersView: FC = () => {
    const { me } = useUserState();
    const [users, setUsers] = useState<User[] | null>(null);
    const [inviting, setInviting] = useState(false);
    const [pendingId, setPendingId] = useState<number>();

    const load = useCallback(() => listUsers().then(setUsers).catch(handleError), []);

    useEffect(() => {
        load();
    }, [load]);

    /** Runs one member action, tells the admin, and reloads the list. */
    const run = async (member: User, action: () => Promise<unknown>, done: string) => {
        setPendingId(member.id);
        try {
            await action();
            message.success(`${member.email} ${done}.`);
            await load();
        } catch (error) {
            handleError(error);
        } finally {
            setPendingId(undefined);
        }
    };

    const setAdmin = (member: User, admin: boolean) =>
        run(
            member,
            () => setUserRoles(member.id, admin ? [UserRole.ADMIN] : []),
            admin ? "is now an admin" : "is no longer an admin"
        );

    const invite = (member: User) =>
        run(member, () => resendInvitation(member.id), "has been sent an invitation");

    const confirmDeactivate = (member: User) =>
        Modal.confirm({
            title: `Deactivate ${member.name}?`,
            content:
                "They are logged out immediately and cannot sign in again until " +
                "they are reactivated. Their projects and documents stay.",
            okText: "Deactivate",
            okButtonProps: { danger: true },
            onOk: () => run(member, () => deactivateUser(member.id), "deactivated"),
        });

    const reactivate = (member: User) =>
        run(member, () => reactivateUser(member.id), "reactivated");

    return (
        <Layout className={classes.layout}>
            <Header className={classes.header}>
                <div className={classes.titleRow}>
                    <div>
                        <h2 className={classes.title}>Members</h2>
                        <div className={classes.subtitle}>
                            Invite your colleagues, decide who administers the organization, and
                            deactivate those who have left.
                        </div>
                    </div>
                    <Button type="primary" size="large" onClick={() => setInviting(true)}>
                        Invite members
                    </Button>
                </div>
            </Header>
            <Content className={classes.content}>
                {users === null ? (
                    <div className={classes.loading}>
                        <Spin size="large" />
                    </div>
                ) : (
                    <MembersTable
                        members={users}
                        selfId={me.id}
                        pendingId={pendingId}
                        onSetAdmin={setAdmin}
                        onInvite={invite}
                        onDeactivate={confirmDeactivate}
                        onReactivate={reactivate}
                    />
                )}
            </Content>
            <InviteUsersModal
                open={inviting}
                onClose={() => setInviting(false)}
                onSent={load}
                invite={(invites) => inviteUsers({ invites })}
            />
        </Layout>
    );
};

MembersView.displayName = "MembersView";
